import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  getAgentAutoUpdateSetting,
  requestAgentUpdateCheck,
  updateAgentAutoUpdateSetting,
} from '@kirin/api-client/api/agent-auto-update';
import {
  AGENT_AUTO_UPDATE_DEFAULT,
  type AgentAutoUpdateSetting,
  type RequestAgentUpdateCheckBody,
  type RequestAgentUpdateCheckResponse,
} from '@kirin/api-client/contracts/agent-auto-update';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';

/**
 * KIR-5191: whether "Automatic agent updates" is On. Until the setting has
 * loaded (or when it cannot be read) the product default applies: On.
 */
export function isAgentAutoUpdateEnabled(
  setting: AgentAutoUpdateSetting | null | undefined
): boolean {
  return setting?.enabled ?? AGENT_AUTO_UPDATE_DEFAULT.enabled;
}

/**
 * KIR-5191: the org's "Automatic agent updates" switch
 * (`GET /organizations/:id/agent-auto-update`, any org role).
 */
export function useAgentAutoUpdateSetting(
  organizationId: string,
  enabled = true
): UseQueryResult<AgentAutoUpdateSetting, unknown> {
  return useQuery({
    queryKey: queryKeys.agentAutoUpdate.setting(organizationId),
    queryFn: () => getAgentAutoUpdateSetting(apiClient, organizationId),
    enabled: enabled && !!organizationId,
    retry: retryHandlers.detail,
  });
}

/**
 * KIR-5191: turn "Automatic agent updates" On or Off (Owners and Managers).
 * Writes the saved setting into the cache, then invalidates everything that
 * reads the switch: the users table (its row state reads `off`) and the
 * per-user endpoints dialog.
 */
export function useUpdateAgentAutoUpdateSetting(
  organizationId: string
): UseMutationResult<AgentAutoUpdateSetting, Error, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean): Promise<AgentAutoUpdateSetting> =>
      updateAgentAutoUpdateSetting(apiClient, organizationId, { enabled }),
    onSuccess: async data => {
      queryClient.setQueryData<AgentAutoUpdateSetting>(
        queryKeys.agentAutoUpdate.setting(organizationId),
        data
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.userSecurityPosture.all,
        }),
        queryClient.invalidateQueries({
          queryKey: ['userEndpoints', organizationId],
        }),
      ]);
    },
  });
}

/**
 * KIR-5191: "Try update now" — asks a user's connected agents, or only the
 * agent on `machineId`, to run an update check at once (Owners and Managers).
 * Best effort: an agent the request does not reach checks at its next
 * scheduled check. The check still passes every backend gate, so the
 * endpoints dialog is refreshed, not updated in place.
 */
export function useRequestAgentUpdateCheck(
  organizationId: string
): UseMutationResult<
  RequestAgentUpdateCheckResponse,
  Error,
  RequestAgentUpdateCheckBody
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      body: RequestAgentUpdateCheckBody
    ): Promise<RequestAgentUpdateCheckResponse> =>
      requestAgentUpdateCheck(apiClient, organizationId, body),
    onSuccess: async (_data, { userId }) => {
      await queryClient.invalidateQueries({
        queryKey: ['userEndpoints', organizationId, userId],
      });
    },
  });
}
